// pages/UsersPage.tsx
import { useState, useMemo } from "react";
import { Search, MoreVertical, Users, Filter, X, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

import MenuModal from "../../../components/ui/MenuModal";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import PageLayout from "../../../layouts/PageListLayout";
import FlexContainer from "../../../components/ui/FlexContainer";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { useAuth, useUsers } from "@cabukaka/hooks";
import UsersList from "../../../components/features/users/UsersList";
import { User } from "@cabukaka/types";

export default function UsersPage() {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const role = currentUser?.role;
    const { users, deleteUser, deleteManyUsers, verifyUser } = useUsers();

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedRole, setSelectedRole] = useState<string>("");
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const filteredUsers = useMemo(() => {
        let result = users;

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            result = result.filter(u =>
                u.username?.toLowerCase().includes(searchLower) ||
                u.email?.toLowerCase().includes(searchLower)
            );
        }

        if (selectedRole) {
            result = result.filter(u => u.role === selectedRole);
        }

        if (selectedStatus) {
            const isVerified = selectedStatus === "verified";
            result = result.filter(u => u.isVerified === isVerified);
        }

        return result;
    }, [users, searchTerm, selectedRole, selectedStatus]);

    const stats = useMemo(() => ({
        total: users.length,
        admins: users.filter(u => u.role === "admin").length,
        shops: users.filter(u => u.role === "shop").length,
        clients: users.filter(u => u.role === "client").length,
        verified: users.filter(u => u.isVerified).length
    }), [users]);

    const handleDelete = async () => {
        if (!userToDelete?.id) return;
        setIsDeleting(true);
        try {
            await deleteUser(userToDelete.id);
            setUserToDelete(null);
            alertSuccess("Utilisateur supprimé avec succès");
        } catch (err) {
            alertError("Erreur lors de la suppression");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteMany = async () => {
        if (!itemsToDelete || itemsToDelete.length === 0) return;
        try {
            await deleteManyUsers(itemsToDelete);
            setItemsToDelete(null);
            alertSuccess(`${itemsToDelete.length} utilisateur(s) supprimé(s)`);
        } catch (err) {
            alertError("Erreur lors de la suppression multiple");
        }
    };

    const clearFilters = () => {
        setSelectedRole("");
        setSelectedStatus("");
        setSearchTerm("");
    };

    const hasActiveFilters = selectedRole || selectedStatus || searchTerm;

    const roleOptions = [
        { value: "admin", label: "Admin" },
        { value: "shop", label: "Vendeur" },
        { value: "client", label: "Client" }
    ];

    const statusOptions = [
        { value: "verified", label: "Vérifié" },
        { value: "unverified", label: "Non vérifié" }
    ];

    return (
        <PageLayout
            title="Gestion des utilisateurs"
            description={`${filteredUsers.length} utilisateur${filteredUsers.length > 1 ? 's' : ''} affiché${filteredUsers.length > 1 ? 's' : ''}`}
            actions={
                <FlexContainer className="animate-fade-in-up w-full">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                        <Filter size={18} /> Filtrer
                        {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
                    </button>
                </FlexContainer>
            }
        >
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                    <p className="text-xs font-semibold text-blue-700/70">Total</p>
                    <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
                    <p className="text-xs font-semibold text-purple-700/70">Admins</p>
                    <p className="text-2xl font-bold text-purple-700">{stats.admins}</p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
                    <p className="text-xs font-semibold text-orange-700/70">Vendeurs</p>
                    <p className="text-2xl font-bold text-orange-700">{stats.shops}</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                    <p className="text-xs font-semibold text-green-700/70">Clients</p>
                    <p className="text-2xl font-bold text-green-700">{stats.clients}</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4">
                    <p className="text-xs font-semibold text-emerald-700/70">Vérifiés</p>
                    <p className="text-2xl font-bold text-emerald-700">{stats.verified}</p>
                </div>
            </div>

            {/* Recherche */}
            <div className="relative mb-4">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Rechercher un utilisateur..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
            </div>

            {/* Filtres */}
            {showFilters && (
                <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-700">Filtres</h3>
                        {hasActiveFilters && (
                            <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                                <X size={14} /> Effacer tout
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                            <select
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            >
                                <option value="">Tous les rôles</option>
                                {roleOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            >
                                <option value="">Tous les statuts</option>
                                {statusOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Liste */}
            <div>
                <UsersList
                    users={filteredUsers}
                    selectable={true}
                    onAction={(u) => setSelectedUser(u)}
                    selectActions={[{
                        label: "Supprimer",
                        onClick: (selected) => setItemsToDelete(selected.map(i => i.id)),
                        className: "bg-red-600 text-white"
                    }]}
                />
            </div>

            {/* Menu modal */}
            {selectedUser && (
                <MenuModal
                    title={selectedUser.username}
                    isOpen={!!selectedUser}
                    onClose={() => setSelectedUser(null)}
                    icon={<Users className="text-primary" size={20} />}
                    menu={[
                        { label: "Voir détails", icon: Search, onClick: () => navigate(`/${role}/users/details?id=${selectedUser.id}`) },
                        ...(!selectedUser.isVerified ? [{ label: "Vérifier", icon: Check, onClick: () => verifyUser(selectedUser.id) }] : []),
                        { label: "Supprimer", icon: MoreVertical, onClick: () => { setUserToDelete(selectedUser); setSelectedUser(null); } }
                    ]}
                />
            )}
            {/* Modals suppression */}
            <DeleteConfirmationModal
                isOpen={!!userToDelete}
                onClose={() => setUserToDelete(null)}
                onConfirm={handleDelete}
                title="Supprimer l'utilisateur"
                message={`Supprimer "${userToDelete?.username}" ?`}
                confirmText={isDeleting ? "Suppression..." : "Supprimer"}
            />
            <DeleteConfirmationModal
                isOpen={!!itemsToDelete && itemsToDelete.length > 0}
                onClose={() => setItemsToDelete(null)}
                onConfirm={handleDeleteMany}
                title="Suppression multiple"
                message={`Supprimer ${itemsToDelete?.length} utilisateur(s) ?`}
            />
        </PageLayout>
    );
}